import { useCallback, useEffect, useState } from 'react';
import { updateBundleFloorTargetQty } from '../services/bundleFloorAPI';
import { getApiErrorMessage } from '../services/errorUtils';
import type { BundleFloorLevel } from '../schemas/apiResponse.schemas';

export interface BundleBaselineUpdate {
  readonly submitting: boolean;
  readonly error: string | null;
  readonly clearError: () => void;
  /** Resolves to the updated floor level, or null when the PATCH failed (see `error`). */
  readonly submit: (targetQty: number) => Promise<BundleFloorLevel | null>;
}

/** Change Baseline save state for one bundle parent. Resets when the SKU changes. */
export function useBundleBaselineUpdate(sku: string): BundleBaselineUpdate {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSubmitting(false);
    setError(null);
  }, [sku]);

  const clearError = useCallback((): void => setError(null), []);

  const submit = useCallback(async (targetQty: number): Promise<BundleFloorLevel | null> => {
    setSubmitting(true);
    setError(null);
    try {
      return await updateBundleFloorTargetQty(sku, targetQty);
    } catch (err: unknown) {
      console.error('Error updating bundle baseline:', err);
      setError(getApiErrorMessage(err, 'Failed to update baseline. Please try again.'));
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [sku]);

  return { submitting, error, clearError, submit };
}
